import {
  collection,
  doc,
  deleteDoc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
  Timestamp as FsTimestamp,
  type CollectionReference,
  type DocumentReference,
  type Firestore,
} from 'firebase/firestore';
import {
  readWbsItemSystems,
  type ProjectDoc,
  type WbsConstraintsDoc,
  type WbsHoliday,
  type WbsItemDoc,
  type WbsItemKind,
  type WbsItemStatus,
  type WbsProgressMode,
} from '@mct/shared';
import { memberFieldsFromProject } from './firestore';
import { computeAppendSortKey } from './wbsResort';
import { validateKindChange } from './wbsKindGuard';

export const wbsItemsCol = (
  db: Firestore,
  pid: string,
): CollectionReference => collection(db, 'projects', pid, 'wbsItems');

export const wbsItemRef = (
  db: Firestore,
  pid: string,
  itemId: string,
): DocumentReference => doc(db, 'projects', pid, 'wbsItems', itemId);

export const wbsConstraintsRef = (
  db: Firestore,
  pid: string,
): DocumentReference =>
  doc(db, 'projects', pid, 'wbsConstraints', 'default');

export interface CreateWbsItemInput {
  itemId?: string;
  kind: WbsItemKind;
  parentItemId: string | null;
  sortKey: string;
  name: string;
  description?: string;
  /** 領域 labels (multi-value). Empty / omitted = 未設定. */
  systems?: string[];
  vendor?: string;
  assigneeId?: string | null;
  plannedStart?: Date | null;
  plannedEnd?: Date | null;
  actualStart?: Date | null;
  actualEnd?: Date | null;
  durationDays?: number;
  progress?: number;
  progressMode?: WbsProgressMode;
  status?: WbsItemStatus;
  dependsOn?: string[];
  backlogIssueKeys?: string[];
}

function toFsTimestamp(d: Date | null | undefined): FsTimestamp | null {
  if (!d) return null;
  return FsTimestamp.fromMillis(d.getTime());
}

/**
 * Milestones are single-date rows. Convention: write the same date into
 * both `plannedStart` and `plannedEnd` so range-based code paths (axis
 * range, sort, parent rollup) keep working without special cases.
 */
export function isMilestoneKind(kind: WbsItemKind): boolean {
  return kind === 'milestone';
}

/**
 * Append a new row to the end of its parent's sibling group, allocating
 * a fractional-indexing sortKey. Caller passes the full current item
 * list so the helper can migrate legacy sibling keys inline. All writes
 * (sibling migrations + the new row) land in one Firestore batch so
 * on-disk we never end up with a mixed legacy/fractional group.
 */
export async function appendWbsItem(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  items: readonly WbsItemDoc[],
  input: Omit<CreateWbsItemInput, 'sortKey'>,
): Promise<string> {
  const { sortKey, migrations } = computeAppendSortKey(
    items,
    input.parentItemId,
  );
  const ref = input.itemId
    ? wbsItemRef(db, project.projectId, input.itemId)
    : doc(wbsItemsCol(db, project.projectId));
  const now = serverTimestamp();
  const payload = buildCreatePayload(ref.id, project, {
    ...input,
    sortKey,
  });
  if (migrations.size === 0) {
    await setDoc(ref, payload);
    return ref.id;
  }
  const batch = writeBatch(db);
  batch.set(ref, payload);
  for (const [itemId, nextKey] of migrations) {
    batch.update(wbsItemRef(db, project.projectId, itemId), {
      sortKey: nextKey,
      updatedAt: now,
      lastModifiedAt: now,
    });
  }
  await batch.commit();
  return ref.id;
}

function buildCreatePayload(
  itemId: string,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  input: CreateWbsItemInput,
): Record<string, unknown> {
  const now = serverTimestamp();
  // For milestones the canonical store is `plannedStart`; mirror to
  // `plannedEnd` so existing range math (`tsToDate(end)` checks etc.)
  // doesn't need to special-case the kind.
  const plannedStart = input.plannedStart ?? null;
  const plannedEnd = isMilestoneKind(input.kind)
    ? plannedStart
    : input.plannedEnd ?? null;
  return {
    itemId,
    projectId: project.projectId,
    kind: input.kind,
    parentItemId: input.parentItemId,
    sortKey: input.sortKey,
    name: input.name,
    description: input.description ?? '',
    systems: input.systems ?? [],
    vendor: input.vendor ?? '',
    assigneeId: input.assigneeId ?? null,
    plannedStart: toFsTimestamp(plannedStart),
    plannedEnd: toFsTimestamp(plannedEnd),
    actualStart: toFsTimestamp(input.actualStart),
    actualEnd: toFsTimestamp(input.actualEnd),
    durationDays: input.durationDays ?? 0,
    progress: input.progress ?? 0,
    progressMode: input.progressMode ?? 'manual',
    status: input.status ?? 'not_started',
    dependsOn: input.dependsOn ?? [],
    backlogIssueKeys: input.backlogIssueKeys ?? [],
    ...memberFieldsFromProject(project),
    createdAt: now,
    updatedAt: now,
    lastModifiedAt: now,
  };
}

export async function createWbsItem(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  input: CreateWbsItemInput,
): Promise<string> {
  const ref = input.itemId
    ? wbsItemRef(db, project.projectId, input.itemId)
    : doc(wbsItemsCol(db, project.projectId));
  const payload = buildCreatePayload(ref.id, project, input);
  await setDoc(ref, payload);
  return ref.id;
}

/**
 * Hard cap on `completionCriteria` length when set from the client.
 * Matches the UX guidance from the design doc — phases that need more
 * detail should link out to dedicated docs rather than swelling the
 * WBS row. Enforced both here (client-side) and at the call site so
 * users get an immediate error instead of a Firestore write rejection.
 */
export const WBS_COMPLETION_CRITERIA_MAX_LENGTH = 5000;

export interface UpdateWbsItemPatch {
  name?: string;
  description?: string;
  /**
   * Multi-line "完了基準" set on phase rows (`kind === 'phase'`).
   * Pass `null` to clear. Capped at
   * {@link WBS_COMPLETION_CRITERIA_MAX_LENGTH} characters; longer
   * strings throw before the Firestore call.
   */
  completionCriteria?: string | null;
  /** 領域 labels (multi-value). Empty array = 未設定. Never write legacy `system`. */
  systems?: string[];
  vendor?: string;
  /**
   * Reference into `/projects/{pid}/assignees` — the canonical
   * assignee field. Set when the user picks from the project-wide
   * assignee list (the same list shown on the migration-task DAG view
   * and the Settings page). `null` clears the link.
   */
  assigneeId?: string | null;
  plannedStart?: Date | null;
  plannedEnd?: Date | null;
  actualStart?: Date | null;
  actualEnd?: Date | null;
  durationDays?: number;
  progress?: number;
  progressMode?: WbsProgressMode;
  status?: WbsItemStatus;
  dependsOn?: string[];
  backlogIssueKeys?: string[];
  sortKey?: string;
  parentItemId?: string | null;
  kind?: WbsItemKind;
}

const DATE_FIELDS = new Set<keyof UpdateWbsItemPatch>([
  'plannedStart',
  'plannedEnd',
  'actualStart',
  'actualEnd',
]);

/**
 * Fields whose presence in a patch means the user is taking authorship
 * back from the Backlog auto-rollup — see
 * `functions/src/core/wbs/backlogRollup.ts`. When either is in the
 * patch we explicitly null `backlogAutoUpdatedAt` so the detail-panel
 * badge disappears.
 */
const BACKLOG_AUTHORSHIP_FIELDS = new Set<keyof UpdateWbsItemPatch>([
  'status',
  'actualEnd',
]);

export async function updateWbsItem(
  db: Firestore,
  pid: string,
  itemId: string,
  patch: UpdateWbsItemPatch,
): Promise<void> {
  if (
    typeof patch.completionCriteria === 'string' &&
    patch.completionCriteria.length > WBS_COMPLETION_CRITERIA_MAX_LENGTH
  ) {
    throw new Error(
      `完了基準は ${WBS_COMPLETION_CRITERIA_MAX_LENGTH} 文字以内で入力してください`,
    );
  }
  const payload: Record<string, unknown> = {};
  let clearsBacklogAuto = false;
  for (const [k, v] of Object.entries(patch) as [
    keyof UpdateWbsItemPatch,
    unknown,
  ][]) {
    if (v === undefined) continue;
    if (BACKLOG_AUTHORSHIP_FIELDS.has(k)) clearsBacklogAuto = true;
    if (DATE_FIELDS.has(k)) {
      payload[k as string] = toFsTimestamp(v as Date | null);
    } else {
      payload[k as string] = v;
    }
  }
  if (clearsBacklogAuto) {
    payload.backlogAutoUpdatedAt = null;
  }
  payload.updatedAt = serverTimestamp();
  payload.lastModifiedAt = serverTimestamp();
  await updateDoc(wbsItemRef(db, pid, itemId), payload);
}

/**
 * Defense-in-depth wrapper around `updateWbsItem` for `kind` changes.
 * The detail-panel UI also runs the same check before calling — this
 * keeps the rule in one place so a future caller can't bypass it by
 * patching `kind` directly through `updateWbsItem`.
 *
 * Throws if the change would violate a hard rule
 * (see `wbsKindGuard.validateKindChange`). Same-kind calls are no-ops.
 * Soft warnings (e.g. phase→task with children) are NOT enforced here —
 * those are a UI concern (confirm dialog) and intentionally let the
 * write through once the user confirms.
 */
export async function updateWbsItemKind(
  db: Firestore,
  pid: string,
  item: Pick<WbsItemDoc, 'itemId' | 'kind' | 'parentItemId'>,
  nextKind: WbsItemKind,
  allItems: readonly Pick<WbsItemDoc, 'itemId' | 'kind' | 'parentItemId'>[],
): Promise<void> {
  if (nextKind === item.kind) return;
  const violation = validateKindChange(item, nextKind, allItems);
  if (violation) {
    throw new Error(violation.message);
  }
  await updateDoc(wbsItemRef(db, pid, item.itemId), {
    kind: nextKind,
    updatedAt: serverTimestamp(),
    lastModifiedAt: serverTimestamp(),
  });
}

/**
 * Apply a DnD move as a single Firestore batch: updates parentItemId for the
 * dragged row (if it changed) and rewrites sortKey for every affected sibling.
 * Caller provides the map produced by `buildMoveWrites`.
 */
export async function applyWbsMoveWrites(
  db: Firestore,
  pid: string,
  updates: ReadonlyMap<
    string,
    { parentItemId?: string | null; sortKey?: string }
  >,
): Promise<void> {
  if (updates.size === 0) return;
  const entries = Array.from(updates.entries());
  for (let i = 0; i < entries.length; i += 400) {
    const chunk = entries.slice(i, i + 400);
    const batch = writeBatch(db);
    for (const [itemId, patch] of chunk) {
      const payload: Record<string, unknown> = { ...patch };
      payload.updatedAt = serverTimestamp();
      payload.lastModifiedAt = serverTimestamp();
      batch.update(wbsItemRef(db, pid, itemId), payload);
    }
    await batch.commit();
  }
}

/** Delete a row and optionally its descendants. The caller must supply the tree. */
export async function deleteWbsItem(
  db: Firestore,
  pid: string,
  itemId: string,
  descendantIds: readonly string[] = [],
): Promise<void> {
  const ids = [itemId, ...descendantIds];
  if (ids.length <= 1) {
    await deleteDoc(wbsItemRef(db, pid, itemId));
    return;
  }
  // Firestore batch limit is 500. Chunk just in case.
  for (let i = 0; i < ids.length; i += 400) {
    const chunk = ids.slice(i, i + 400);
    const batch = writeBatch(db);
    for (const id of chunk) {
      batch.delete(wbsItemRef(db, pid, id));
    }
    await batch.commit();
  }
}

export interface UpdateWbsConstraintsPatch {
  holidays?: WbsHoliday[];
  notes?: string[];
}

export async function updateWbsConstraints(
  db: Firestore,
  pid: string,
  patch: UpdateWbsConstraintsPatch,
): Promise<void> {
  await updateDoc(wbsConstraintsRef(db, pid), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

/** Create the constraints doc in full (used by the client-side import fallback). */
export async function setWbsConstraints(
  db: Firestore,
  project: Pick<ProjectDoc, 'projectId' | 'memberUserIds' | 'memberRoles'>,
  data: Pick<WbsConstraintsDoc, 'holidays' | 'notes'>,
): Promise<void> {
  await setDoc(wbsConstraintsRef(db, project.projectId), {
    projectId: project.projectId,
    ...data,
    ...memberFieldsFromProject(project),
    updatedAt: serverTimestamp(),
  });
}

export async function getWbsConstraints(
  db: Firestore,
  pid: string,
): Promise<WbsConstraintsDoc | null> {
  const snap = await getDoc(wbsConstraintsRef(db, pid));
  return snap.exists() ? (snap.data() as WbsConstraintsDoc) : null;
}

/**
 * Rename every WBS row that uses option `from` to `to`, in chunked
 * batches. When `to === null` the option is removed from the row.
 * Callers pass the rows so the scan is client-side (no extra reads).
 *
 * The 領域 field (`'system'`) is now the multi-value `systems` array, so
 * a rename/delete updates the row's whole array (mapping the matched
 * label and dropping it on delete) and writes only the canonical
 * `systems` field. The vendor field stays a single string.
 */
export async function renameWbsItemOption(
  db: Firestore,
  pid: string,
  field: 'system' | 'vendor',
  from: string,
  to: string | null,
  items: readonly {
    itemId: string;
    systems?: readonly string[] | null;
    system?: string | null;
    vendor?: string | null;
  }[],
): Promise<number> {
  if (field === 'system') {
    // 領域: operate on the multi-value `systems` array via dual-read.
    const targets = items.filter((i) => readWbsItemSystems(i).includes(from));
    if (targets.length === 0) return 0;
    for (let i = 0; i < targets.length; i += 400) {
      const chunk = targets.slice(i, i + 400);
      const batch = writeBatch(db);
      for (const t of chunk) {
        const current = readWbsItemSystems(t);
        const next =
          to === null
            ? current.filter((s) => s !== from)
            : // rename: replace the matched label, dedupe in case `to`
              // already present on the same row.
              Array.from(
                new Set(current.map((s) => (s === from ? to : s))),
              );
        batch.update(wbsItemRef(db, pid, t.itemId), {
          systems: next,
          updatedAt: serverTimestamp(),
          lastModifiedAt: serverTimestamp(),
        });
      }
      await batch.commit();
    }
    return targets.length;
  }
  const targets = items.filter((i) => (i.vendor ?? '') === from);
  if (targets.length === 0) return 0;
  for (let i = 0; i < targets.length; i += 400) {
    const chunk = targets.slice(i, i + 400);
    const batch = writeBatch(db);
    for (const t of chunk) {
      batch.update(wbsItemRef(db, pid, t.itemId), {
        vendor: to ?? '',
        updatedAt: serverTimestamp(),
        lastModifiedAt: serverTimestamp(),
      });
    }
    await batch.commit();
  }
  return targets.length;
}
